import { type LoaderFunctionArgs, json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { Link } from '#app/components/navigation/link.tsx'
import { H2 } from '#app/components/typography/h2.tsx'
import { H3 } from '#app/components/typography/h3.tsx'
import { P } from '#app/components/typography/p.tsx'
import { Ul } from '#app/components/typography/ul.tsx'
import { Code } from '#app/old_components/typography/code.tsx'
import { CodeBlock } from '#app/old_components/ui/code-block.tsx'
import { sessionKey } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { authSessionStorage } from '#app/utils/session.server.ts'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Source, type componentProps, Styling } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Link',
}

const component: componentProps = {
	name: 'Link',
	shortName: 'Link',
	fileName: 'link',
	category: 'navigation',
	description: ['A semantic element for navigation between pages.'],
	features: [
		'Support for HTML navigation links, JavaScript handled links, and client side routing.',
		'Disabled links are also supported.',
		'Consistent cross-browser hover, press, and keyboard focus states.',
		'Implemented as a custom ARIA link when handled via JavaScript.',
		'Easy styling.',
	],
	parts: [
		{
			name: 'Link',
			description: 'A semantic element for navigation between pages.',
		},
	],
	usage: `<P>
	<Link to="/components/navigation/link" prefetch="intent">
		{({ isActive }) => (isActive ? 'This is active link' : 'This is an inactive link')}
	</Link>{' '}
	while{' '}
	<Link to="/components/navigation/breadcrumbs" prefetch="intent">
		{({ isActive }) => (isActive ? 'this is active link' : 'this is an inactive link')}
	</Link>
</P>`,
	styling: `/* ### Navigation ### */
@layer components {
	.link {
		@apply underline;
	}
}`,
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const authSession = await authSessionStorage.getSession(request.headers.get('cookie'))
	const sessionId = authSession.get(sessionKey)
	const session =
		sessionId ?
			await prisma.session.findUnique({
				select: { user: { select: { id: true, paid: true } } },
				where: { id: sessionId, expirationDate: { gt: new Date() } },
			})
		:	null
	const source = session?.user.paid ? await readSource({ category: component.category, fileName: component.fileName }) : null
	return json({ source })
}

const LinkRoute = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, usage, shortName, fileName, styling } = component

	return (
		<>
			<Description name={name} description={description} />
			<H2>Notes</H2>
			<P>
				There are 3 types of links: a plain old <Code>{'<a>'}</Code> tag, your framework's <Code>{'<Link />'}</Code> component, and React Aria's <Code>{'<Link />'}</Code> component.
				Let's take a look at each one.
			</P>

			<H3>
				Plain old <Code>{'<a>'}</Code> tags
			</H3>
			<P>
				Plain old <Code>{'<a>'}</Code> tags should be used when linking outside your app.
			</P>

			<H3>
				Your framework's <Code>{'<Link />'}</Code> component
			</H3>
			<P>
				Your framework's <Code>{'<Link />'}</Code> component should be used when linking inside your app. This component is responsible for:
			</P>
			<Ul>
				<li>
					doing an internal <Code>pushState</Code>
				</li>
				<li>resolving relative route paths</li>
				<li>prefetching app assets</li>
				<li>adding active/pending styles/classes</li>
				<li>
					adding <Code>location.state</Code>
				</li>
			</Ul>
			<P>
				None of which have anything to do with an <Code>{'<a>'}</Code> that links to an external route. You can't <Code>pushState</Code> to a different origin, resolve relative paths,
				prefetch, add location state, or add active/pending styles when going there. For those, you'll need to use a plain old <Code>{'<a>'}</Code> tag, instead of you framework's{' '}
				<Code>{'<Link>'}</Code> component.
			</P>
			<P>
				This being said, most frameworks's <Code>{'<Link />'}</Code> components (like Next and Remix) detects whether the link is internal or external so you don't need to remember to
				either use a plain-old <Code>{'<a>'}</Code> tag or your framework's <Code>{'<Link />'}</Code> component depending on whether your link is internal or external. Just use your
				frameworks's <Code>{'<Link />'}</Code> and it will do the right thing.
			</P>
			<H3>
				React Aria's <Code>{'<Link />'}</Code> component
			</H3>
			<P>
				React Aria also has its own <Code>{'<Link />'}</Code> component with its own functionality.
			</P>
			<P>
				Unfortunately complete integration between your framework's <Code>{'<Link />'}</Code> and React Aria's <Code>{'<Link />'}</Code> is{' '}
				<a href="https://github.com/adobe/react-spectrum/issues/5476" target="_blank" rel="noreferrer">
					a hard problem that isn't yet solved
				</a>
				. We can't yet have React Aria <Code>{'<Link />'}</Code>'s with prefetching in Remix, for example.
			</P>
			<P>
				But we can have partial integration, insofar as we can get React Aria <Code>{'<Link />'}</Code> to use client-side routing, as opposed to a full-page reload - which is what
				happens when you use a React Aria <Code>{'<Link />'}</Code> without any integration. So please follow{' '}
				<a href="https://react-spectrum.adobe.com/react-aria/routing.html" target="_blank" rel="noreferrer">
					this guide
				</a>{' '}
				to integrate React Aria links with your framework.
			</P>
			<H3>State of the art</H3>
			<P>
				So we can't yet have a unified <Code>{'<Link />'}</Code> component.
			</P>
			<P>
				The <Code>{'<Link />'}</Code> component described in this page is your framework's <Code>{'<Link />'}</Code> component which will be used to navigate around your app (and outside
				it).
			</P>
			<P>
				You'll be implicitly using React Aria's <Code>{'<Link />'}</Code> when importing from components that have links. For example, the <Code>{'<Breadcrumbs />'}</Code> component has
				a <Code>{'<BreadcrumbsLink />'}</Code> component that's just a React Aria's <Code>{'<Link />'}</Code> component under the hood. Just keep in mind that, unlike this{' '}
				<Code>{'<Link />'}</Code>, those links won't be able to do framework-specific things like prefetching or supporting functional props. At least not yet.
			</P>
			<H2>Features</H2>
			<P>
				The features of this component are dependent on your framework of choice. If you have a choice between choosing a <Code>{'<Link />'}</Code> component and a navigation-aware{' '}
				<Code>{'<NavLink />'}</Code>, choose the <Code>{'<NavLink />'}</Code> so you can take advantage of the active/pending states.
			</P>
			<H2>Usage</H2>
			<CodeBlock code={usage} filename={`${shortName}Example`} extension="tsx" className="mt-4" />
			<P className="mt-4">
				Here's the <Code>{`<${shortName} />`}</Code> component in action, using the <Code>{'<NavLink />'}</Code> component from Remix under the hood.
			</P>
			<P>
				<Link to="/components/navigation/link" prefetch="intent">
					{({ isActive }) => (isActive ? 'This is active link' : 'This is an inactive link')}
				</Link>{' '}
				while{' '}
				<Link to="/components/navigation/breadcrumbs" prefetch="intent">
					{({ isActive }) => (isActive ? 'this is active link' : 'this is an inactive link')}
				</Link>
			</P>
			<Source source={source} fileName={fileName} />
			<Styling styling={styling} />
		</>
	)
}
export default LinkRoute
